import React, { KeyboardEvent, useState } from 'react';
import Input, { PasswordInput } from '@ui-library/input';
import classes from './userDetails.module.scss';
import { duotone } from '@fortawesome/fontawesome-svg-core/import.macro';
import auth from '@services/cognito';
import Button from '@ui-library/button/index';
import SetupAccountHeader from '../header';
import { getParsedValue, getApiErrorMessage, isValidInput } from '@components/common/util/util';
import { FREETRIAL } from '@auth/common/constants';
import queryString from 'query-string';
import LocalStorage from '@utils/localStorage';
import { CatchErr } from '@utils/types';
import useStores from '@stores/useStores';
import { useLocation } from 'react-router-dom';
import { Flex } from '@ui-library/misc';
import Requirements from '@components/profile/account/security/passwordSetup/requirements';
import { PopupContainer } from '../styles';
import { validateOrgName } from '../utils';
import { useCreatePassword } from '../hooks';

interface Props {
  token: string;
  updatePage: () => void;
}

interface IErrorsState {
  errorMessage?: string;
  orgNameErrorMessage?: string;
}
const UserDetails = (props: Props) => {
  const { userStore } = useStores();
  const location = useLocation();
  const [name, setName] = useState<string>('');
  const [organization, setOrganization] = useState<string>('');
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [errors, setErrors] = useState<IErrorsState>({
    errorMessage: '',
  });
  const [loading, setLoading] = useState<boolean>(false);

  const {
    password,
    confirmPassword,
    onPasswordChange,
    onPasswordBlur,
    onConfirmPasswordChange,
    passwordError,
    confirmError,
    updateConfirmPasswordError,
  } = useCreatePassword();

  const updateErrors = (details: Partial<IErrorsState>) => {
    setErrors({
      ...errors,
      ...details,
    });
  };

  const handleSubmit = async () => {
    const { token } = props;
    setLoading(true);
    updateErrors({ errorMessage: '' });

    const parsed = queryString.parse(location.search);
    const freetrial = getParsedValue(parsed, 'type') === FREETRIAL;
    const isBlendoUser = LocalStorage.isFlagSet('isBlendoUser');
    try {
      await auth.signup({ name, password, organization, token, freetrial, isBlendoUser });
      userStore!.signUp(name, organization);

      const loginResp = await auth.elogin(token, password);
      await userStore?.loginUser({
        accessToken: loginResp.accessToken,
        idToken: loginResp.idToken,
        refreshToken: loginResp.refreshToken,
      });
      setLoading(false);
      props.updatePage();
      window.rudderanalytics.track('signup success', {
        name,
        organization,
        freetrial,
      });
    } catch (error: CatchErr) {
      setLoading(false);
      updateConfirmPasswordError(getApiErrorMessage(error, 'Unable to Signup'));
      window.rudderanalytics.track('signup failure', {
        name,
        organization,
        freetrial,
        error,
      });
    }
  };
  const enableSubmit =
    name &&
    organization &&
    password &&
    confirmPassword &&
    password === confirmPassword &&
    !errors.errorMessage &&
    !passwordError &&
    !confirmError &&
    !errors.orgNameErrorMessage;

  const handleKeyPress = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' && enableSubmit) {
      handleSubmit();
    }
  };

  const onChangePassword = (value: string) => {
    setShowPopup(true);
    onPasswordChange(value);
  };
  const onBlurPassword = (value: string) => {
    setShowPopup(false);
    onPasswordBlur(value);
  };

  const onFullNameBlur = (value: string) => {
    if (!isValidInput('^[A-Za-z-_. 0-9]{2,50}$', value)) {
      // This is the same regex which is being used in backend
      updateErrors({
        errorMessage: 'Please enter a valid name',
      });
    }
    setName(value);
  };
  const onNameChange = (value: string) => {
    updateErrors({
      errorMessage: '',
    });
    setName(value);
  };
  const onOrganisationChange = (value: string) => {
    updateErrors({
      orgNameErrorMessage: '',
    });
    setOrganization(value);
  };
  const onOrganisationBlur = (value: string) => {
    if (!validateOrgName(value)) {
      updateErrors({
        orgNameErrorMessage: 'Please enter a valid organization name',
      });
    } else {
      updateErrors({
        orgNameErrorMessage: '',
      });
    }
    setOrganization(value);
  };

  return (
    <Flex flexDirection="column">
      <SetupAccountHeader
        icon={duotone('hand-wave')}
        header="Welcome to RudderStack!"
        description="Let’s get started by setting up your account."
      />

      <Input
        label="Full name"
        id="text-input-name"
        data-testid="full-name"
        placeholder="Full Name"
        errorMessageClass={classes.passwordError}
        errorMessage={errors.errorMessage}
        isError={!!errors.errorMessage}
        onBlur={(e) => onFullNameBlur(e.target.value)}
        onChange={(e) => onNameChange(e.target.value)}
        maxLength={50}
        value={name}
      />
      <Input
        isError={!!errors.orgNameErrorMessage}
        errorMessage={errors.orgNameErrorMessage}
        errorMessageClass={classes.passwordError}
        label="Company name"
        id="text-input-organization"
        data-testid="company-name"
        placeholder="Company Name"
        onChange={(e) => onOrganisationChange(e.target.value)}
        onBlur={(e) => onOrganisationBlur(e.target.value)}
        maxLength={128}
        value={organization}
      />
      <div>
        <PasswordInput
          testId="password-field"
          label="Password"
          onBlur={(e) => onBlurPassword(e.target.value)}
          errorMessageClass={classes.passwordError}
          errorMessage={passwordError}
          isError={!!passwordError}
          onChange={(e) => onChangePassword(e.target.value)}
          onKeyDown={handleKeyPress}
          value={password}
        />
        {showPopup && (
          <div className={classes.popup}>
            <PopupContainer className={classes.leftTriangle} />
            <PopupContainer className={classes.container}>
              <Requirements password={password} />
            </PopupContainer>
          </div>
        )}
      </div>

      <PasswordInput
        testId="confirm-password-field"
        errorMessageClass={classes.passwordError}
        isError={!!confirmError}
        errorMessage={confirmError}
        label="Confirm password"
        onChange={(e) => onConfirmPasswordChange(e.target.value)}
        onKeyDown={handleKeyPress}
        value={confirmPassword}
      />
      <Button
        data-testid="create-account"
        disabled={!enableSubmit}
        className={classes.submitButton}
        onClick={handleSubmit}
        type="primary"
        loading={loading}
      >
        Create Account
      </Button>
    </Flex>
  );
};

export default UserDetails;
